import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Copy, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, DOC_TYPES, fmtDate } from "@/lib/api";
import { useDocument, useSettings } from "@/hooks/useData";
import { DocumentPreview } from "@/components/DocumentPreview";
import { PreviewFrame } from "@/components/PreviewFrame";
import { ExportButtons, HiddenPrintable, useDocumentExport } from "@/components/DocumentExport";
import { StatusBadge } from "@/components/StatusBadge";
import { EmailDialog } from "@/components/EmailDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function DocumentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: doc, isLoading } = useDocument(id);
  const { data: settings } = useSettings();
  const [confirm, setConfirm] = useState(false);
  const exp = useDocumentExport(doc);

  if (isLoading || !doc || !settings) return <div className="overline animate-pulse">Loading document</div>;
  const shareUrl = `${window.location.origin}/share/${doc.share_token}`;

  const remove = async () => {
    await api.delete(`/documents/${id}`);
    qc.invalidateQueries({ queryKey: ["documents"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
    toast.success("Document deleted");
    navigate("/documents");
  };

  const duplicate = async () => {
    const { data } = await api.post(`/documents/${id}/duplicate`);
    qc.invalidateQueries({ queryKey: ["documents"] });
    toast.success(`Duplicated as ${data.number}`);
    navigate(`/documents/${data.id}/edit`);
  };

  return (
    <div className="rise" data-testid="document-view">
      <div className="flex flex-wrap items-start justify-between gap-6 mb-8">
        <div className="flex items-start gap-4">
          <Link to="/documents" data-testid="view-back" className="h-10 w-10 grid place-items-center border border-border hover:border-foreground transition-colors"><ArrowLeft className="w-4 h-4" strokeWidth={1.5} /></Link>
          <div>
            <div className="flex items-center gap-3 mb-1"><p className="overline">{DOC_TYPES[doc.type].label}</p><StatusBadge status={doc.status} /></div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight font-mono" data-testid="view-number">{doc.number}</h1>
            <p className="text-sm text-muted-foreground mt-1">{doc.party?.name} · {fmtDate(doc.issue_date)}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/documents/${id}/edit`} data-testid="edit-document-button" className="btn-press h-10 px-4 text-sm font-semibold border border-foreground hover:bg-foreground hover:text-background flex items-center gap-2"><Pencil className="w-4 h-4" strokeWidth={1.5} /> Edit</Link>
          <button onClick={duplicate} data-testid="duplicate-document-button" className="btn-press h-10 px-4 text-sm font-semibold border border-border hover:border-foreground flex items-center gap-2"><Copy className="w-4 h-4" strokeWidth={1.5} /> Duplicate</button>
          <button onClick={() => setConfirm(true)} data-testid="delete-document-button" className="btn-press h-10 px-4 text-sm font-semibold border border-border hover:border-destructive hover:text-destructive flex items-center gap-2"><Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete</button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2"><ExportButtons exp={exp} shareUrl={shareUrl} /><EmailDialog doc={doc} exp={exp} /></div>

      <div className="bg-muted p-4 sm:p-8 border border-border">
        <PreviewFrame>{(scale) => <DocumentPreview doc={doc} settings={settings} scale={scale} />}</PreviewFrame>
      </div>
      <HiddenPrintable exp={exp} doc={doc} settings={settings} />

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete {doc.number}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the document. Shared links will stop working.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-none bg-destructive hover:bg-destructive/90" data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
